import { useMemo } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { Text } from "../Text";
import { AiProviderSpendDonut } from "./AiProviderSpendDonut";
import { computeAiProviderAllocationRows } from "./aiProviderMonitoring";
import { type AiUsageRecord, formatEstimatedCostUsd } from "./aiUsage";
import { spaceColorMap, spaceTrackColorMap } from "./monitoringPanelLayout";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { Panel, PanelHeader, TotalRow } from "./workflowsLayout";

type Props = {
  providerId: string | null;
  limitAmount: number | null;
  spendUsd: number | null;
  spaceBudgets: readonly {
    id: string;
    scopeId: string;
    limitAmount: number | null;
  }[];
  usageRecords: readonly AiUsageRecord[];
  prices: ReadonlyMap<string, number>;
};

export function AiSpendVsLimitsPanel({
  providerId,
  limitAmount,
  spendUsd,
  spaceBudgets,
  usageRecords,
  prices,
}: Props) {
  const [allSpaces] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const displayName = useSpaceDisplayName();
  const teamSpaces = useMemo(
    () =>
      (allSpaces ?? [])
        .filter((space) => space.mode !== "personal")
        .map((space) => ({ id: space.id, name: displayName(space) })),
    [allSpaces, displayName]
  );
  const colorMap = useMemo(() => spaceColorMap(allSpaces ?? []), [allSpaces]);
  const trackColorMap = useMemo(
    () => spaceTrackColorMap(allSpaces ?? []),
    [allSpaces]
  );
  const allocationRows = useMemo(
    () =>
      computeAiProviderAllocationRows(
        providerId,
        usageRecords,
        prices,
        teamSpaces,
        spaceBudgets,
        limitAmount,
        spendUsd
      ),
    [
      providerId,
      usageRecords,
      prices,
      teamSpaces,
      spaceBudgets,
      limitAmount,
      spendUsd,
    ]
  );

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Spend vs limits</Text>
        <TotalRow>
          <Text variant="display-md">
            {formatEstimatedCostUsd(spendUsd ?? 0)}
          </Text>
        </TotalRow>
      </PanelHeader>
      <AiProviderSpendDonut
        rows={allocationRows}
        colorMap={colorMap}
        trackColorMap={trackColorMap}
      />
    </Panel>
  );
}
